import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import Swal from "sweetalert2";
import "./StaffAdjustments.css";

const money = (value) => {
  const amount = Number(value) || 0;
  return `Rs ${amount.toLocaleString("en-PK")}`;
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const kindFromPath = (pathname) => {
  if (String(pathname).includes("fine")) return "fine";
  if (String(pathname).includes("advance")) return "advance";
  return "loan";
};

export default function StaffAdjustments() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const location = useLocation();
  const canPickBranch = ["1", "12"].includes(String(role ?? ""));
  const [branch, setBranch] = useState(branch_id ? String(branch_id) : "15");
  const [month, setMonth] = useState(currentMonth());
  const [staffId, setStaffId] = useState("");
  const [names, setNames] = useState([]);
  const [board, setBoard] = useState(null);
  const [kind, setKind] = useState(kindFromPath(location.pathname));
  const [amount, setAmount] = useState("");
  const [term, setTerm] = useState("3");
  const [reason, setReason] = useState("");
  const [formStaff, setFormStaff] = useState("");
  const [formMonth, setFormMonth] = useState(currentMonth());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadStaff = async (branchValue) => {
    try {
      const response = await UserServices.staffName(branchValue || "");
      const rows = response?.data?.data || [];
      setNames(
        rows.map((row) => ({
          id: String(row.id),
          name: `${row.first_name || ""} ${row.last_name || ""}`.trim(),
        }))
      );
    } catch (err) {
      setNames([]);
    }
  };

  const loadBoard = async (nextMonth = month, nextBranch = branch, nextStaff = staffId) => {
    setLoading(true);
    try {
      const response = await UserServices.getPayrollAdjustments({
        month: nextMonth,
        branch_id: nextBranch,
        staff_id: nextStaff,
      });
      setBoard(response.data);
    } catch (err) {
      setBoard(null);
      const message = err?.response?.data?.message || "The salary deductions could not be loaded.";
      Swal.fire({ icon: "error", title: "Could not load", text: message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff(canPickBranch ? branch : branch_id);
    loadBoard(month, canPickBranch ? branch : branch_id, staffId);
    // The board is reloaded by the filter handlers after the first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needle = search.trim().toLowerCase();
  const matches = (name) => !needle || String(name || "").toLowerCase().includes(needle);

  const slipRows = useMemo(() => {
    if (!board) return [];
    const rows = [];
    (board.slip?.advances || []).forEach((row) => {
      if (matches(row.staff_name)) rows.push({ ...row, kind: "Advance" });
    });
    (board.slip?.fines || []).forEach((row) => {
      if (matches(row.staff_name)) rows.push({ ...row, kind: "Penalty" });
    });
    (board.slip?.installments || []).forEach((row) => {
      if (matches(row.staff_name)) rows.push({ ...row, kind: "Loan installment" });
    });
    return rows;
  }, [board, needle]);

  const loans = useMemo(() => {
    return (board?.loans || []).filter((loan) => matches(loan.staff_name));
  }, [board, needle]);

  const save = async (event) => {
    event.preventDefault();
    if (!formStaff || !amount) {
      Swal.fire({ icon: "warning", title: "Missing details", text: "Choose a staff member and enter an amount." });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        staff_id: formStaff,
        amount,
        month: formMonth,
        reason,
        term,
      };
      if (kind === "fine") await UserServices.addPayrollFine(payload);
      else if (kind === "loan") await UserServices.addPayrollLoan(payload);
      else await UserServices.addPayrollAdvance(payload);
      setAmount("");
      setReason("");
      setMonth(formMonth);
      await loadBoard(formMonth, canPickBranch ? branch : branch_id, staffId);
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: kind === "loan"
          ? "The loan is split into monthly installments on the salary."
          : "It will be deducted on that month's salary slip.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Not saved",
        text: err?.response?.data?.message || "Check the amount and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (label, request) => {
    const confirmed = await Swal.fire({
      icon: "question",
      title: label,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    });
    if (!confirmed.isConfirmed) return;
    setSaving(true);
    try {
      await request();
      await loadBoard();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Not updated",
        text: err?.response?.data?.message || "That change could not be saved.",
      });
    } finally {
      setSaving(false);
    }
  };

  const period = board?.period;
  const totals = board?.slip?.totals || { advance: 0, fine: 0, loan: 0 };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="pf-page">
            <div className="pf-head">
              <div>
                <p className="pf-kicker">Salary deductions</p>
                <h1>Advances, loans and penalties</h1>
                <p>
                  An advance or penalty comes off that month's salary. A loan is split into
                  monthly installments, and each installment comes off the salary for its month.
                </p>
              </div>
            </div>

            <div className="pf-filters">
              <label className="pf-field">
                <span>Salary month</span>
                <input
                  type="month"
                  value={month}
                  onChange={(event) => {
                    setMonth(event.target.value);
                    setFormMonth(event.target.value);
                    loadBoard(event.target.value, canPickBranch ? branch : branch_id, staffId);
                  }}
                />
              </label>
              {canPickBranch && (
                <label className="pf-field">
                  <span>Branch</span>
                  <select
                    value={branch}
                    onChange={(event) => {
                      setBranch(event.target.value);
                      setStaffId("");
                      setFormStaff("");
                      loadStaff(event.target.value);
                      loadBoard(month, event.target.value, "");
                    }}
                  >
                    <option value="">All branches</option>
                    <option value="15">F-11</option>
                    <option value="1">G-13</option>
                  </select>
                </label>
              )}
              <label className="pf-field">
                <span>Staff</span>
                <select
                  value={staffId}
                  onChange={(event) => {
                    setStaffId(event.target.value);
                    loadBoard(month, canPickBranch ? branch : branch_id, event.target.value);
                  }}
                >
                  <option value="">All staff</option>
                  {names.map((person) => (
                    <option key={person.id} value={person.id}>{person.name}</option>
                  ))}
                </select>
              </label>
              <label className="pf-field grow">
                <span>Find on this page</span>
                <input
                  value={search}
                  placeholder="Type a name"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
            </div>

            <form className="pf-form" onSubmit={save}>
              <div className="pf-kinds">
                <button type="button" className={kind === "advance" ? "active" : ""} onClick={() => setKind("advance")}>
                  Advance
                  <small>Deducted this month</small>
                </button>
                <button type="button" className={kind === "loan" ? "active" : ""} onClick={() => setKind("loan")}>
                  Loan
                  <small>Monthly installments</small>
                </button>
                <button type="button" className={kind === "fine" ? "active" : ""} onClick={() => setKind("fine")}>
                  Penalty
                  <small>Shown on the slip</small>
                </button>
              </div>
              <div>
                <div className="pf-grid">
                  <label className="pf-field">
                    <span>Staff member</span>
                    <select value={formStaff} onChange={(event) => setFormStaff(event.target.value)}>
                      <option value="">Select staff</option>
                      {names.map((person) => (
                        <option key={`form-${person.id}`} value={person.id}>{person.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pf-field">
                    <span>Amount</span>
                    <input
                      type="number"
                      min="1"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                    />
                  </label>
                  {kind === "loan" ? (
                    <label className="pf-field">
                      <span>Months</span>
                      <input
                        type="number"
                        min="1"
                        max="36"
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                      />
                    </label>
                  ) : (
                    <label className="pf-field">
                      <span>Deduct in</span>
                      <input type="month" value={formMonth} onChange={(event) => setFormMonth(event.target.value)} />
                    </label>
                  )}
                </div>
                {kind === "loan" && (
                  <label className="pf-field" style={{ marginTop: 12 }}>
                    <span>First deduction month</span>
                    <input type="month" value={formMonth} onChange={(event) => setFormMonth(event.target.value)} />
                  </label>
                )}
                <label className="pf-field" style={{ marginTop: 12 }}>
                  <span>Reason</span>
                  <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
                </label>
                <div className="pf-actions">
                  <p className="pf-note">
                    {kind === "loan"
                      ? "Mark an installment Received if it was collected another way. Carry forward moves that installment and every later one by one month."
                      : "Carry forward moves this deduction onto next month's salary slip."}
                  </p>
                  <button className="pf-save" type="submit" disabled={saving}>
                    {saving ? "Saving..." : "Add to salary"}
                  </button>
                </div>
              </div>
            </form>

            <div className="pf-totals">
              <div className="pf-total">
                <span>Advances on this slip</span>
                <strong>{money(totals.advance)}</strong>
              </div>
              <div className="pf-total">
                <span>Penalties on this slip</span>
                <strong>{money(totals.fine)}</strong>
              </div>
              <div className="pf-total">
                <span>Loan installments on this slip</span>
                <strong>{money(totals.loan)}</strong>
              </div>
            </div>

            <section className="pf-panel">
              <h2>{period ? `${period.label} salary slip` : "This month's salary slip"}</h2>
              <p className="pf-note">
                {period
                  ? `These amounts are deducted between ${period.start_date} and ${period.end_date}.`
                  : "Choose a month to see what will be deducted."}
                {loading ? " Loading..." : ""}
              </p>
              {slipRows.length === 0 ? (
                <p className="pf-empty">Nothing is deducted from this month yet.</p>
              ) : (
                <table className="pf-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Type</th>
                      <th>Detail</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {slipRows.map((row) => (
                      <tr key={`${row.kind}-${row.id || row.loan_id}`}>
                        <td>{row.staff_name}</td>
                        <td>{row.kind}</td>
                        <td>
                          {row.kind === "Loan installment" ? (
                            <span className={row.status === "0" ? "pf-pill received" : "pf-pill due"}>
                              {row.legacy ? "Older loan" : row.status_label}
                            </span>
                          ) : (
                            row.reason || "Salary deduction"
                          )}
                          {row.kind === "Loan installment" && row.reason ? ` · ${row.reason}` : ""}
                        </td>
                        <td className="pf-money">{money(row.amount)}</td>
                        <td>
                          <div className="pf-row-actions">
                            {row.kind === "Loan installment" && row.id && row.status === "1" && (
                              <>
                                <button
                                  type="button"
                                  className="pf-link"
                                  disabled={saving}
                                  onClick={() => runAction("Mark this installment received?", () => UserServices.receiveLoanInstallment(row.id))}
                                >
                                  Received
                                </button>
                                <button
                                  type="button"
                                  className="pf-quiet"
                                  disabled={saving}
                                  onClick={() => runAction("Carry this installment and the later ones to the next month?", () => UserServices.carryLoanInstallment(row.id))}
                                >
                                  Carry forward
                                </button>
                              </>
                            )}
                            {row.kind !== "Loan installment" && (
                              <>
                                <button
                                  type="button"
                                  className="pf-quiet"
                                  disabled={saving}
                                  onClick={() => runAction("Move this deduction to next month?", () => UserServices.carryPayrollFinance(row.id))}
                                >
                                  Carry forward
                                </button>
                                <button
                                  type="button"
                                  className="pf-link"
                                  disabled={saving}
                                  onClick={() => runAction("Remove this from the salary?", () => UserServices.removePayrollFinance(row.id))}
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="pf-panel">
              <h2>Loan schedules</h2>
              <p className="pf-note">
                Received installments stay off the salary. Carrying one forward also moves every installment after it.
              </p>
              {loans.length === 0 ? (
                <p className="pf-empty">No open loan for this view.</p>
              ) : (
                <div className="pf-loans">
                  {loans.map((loan) => (
                    <article className="pf-loan" key={loan.id}>
                      <div className="pf-loan-head">
                        <div>
                          <h3>{loan.staff_name}</h3>
                          <p>
                            {money(loan.amount)} over {loan.term} months
                            {loan.reason ? ` · ${loan.reason}` : ""}
                            {loan.received ? ` · received ${money(loan.received)}` : ""}
                          </p>
                        </div>
                        {!loan.legacy && (
                          <button
                            type="button"
                            className="pf-link"
                            disabled={saving}
                            onClick={() => runAction("Remove the remaining installments from salary?", () => UserServices.removePayrollLoan(loan.id))}
                          >
                            Remove remaining
                          </button>
                        )}
                      </div>
                      {loan.legacy ? (
                        <p className="pf-empty">This older loan deducts its installment in the start month only.</p>
                      ) : (
                        <table className="pf-table">
                          <tbody>
                            {loan.installments.map((installment) => (
                              <tr key={installment.id}>
                                <td>{installment.month_label}</td>
                                <td className="pf-money">{money(installment.amount)}</td>
                                <td>
                                  <span className={installment.status === "0" ? "pf-pill received" : "pf-pill due"}>
                                    {installment.on_this_slip ? "On this slip" : installment.status_label}
                                  </span>
                                </td>
                                <td>
                                  {installment.status === "1" && (
                                    <div className="pf-row-actions">
                                      <button
                                        type="button"
                                        className="pf-link"
                                        disabled={saving}
                                        onClick={() => runAction("Mark this installment received?", () => UserServices.receiveLoanInstallment(installment.id))}
                                      >
                                        Received
                                      </button>
                                      <button
                                        type="button"
                                        className="pf-quiet"
                                        disabled={saving}
                                        onClick={() => runAction("Carry this installment and the later ones to the next month?", () => UserServices.carryLoanInstallment(installment.id))}
                                      >
                                        Carry forward
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
